import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import React, { useMemo } from 'react';
import 'prop-types';
import { jsx } from '@emotion/core';
import merge from 'deepmerge';
import progressMultiStep from '../../styles/progressMultiStep.js';
import progressBarStyleObject from '../../styles/progressBar.js';
import { hiddenSmDown } from '../../styles/layout/hiddenSmDown.js';
import { body1 } from '../../styles/typography/body1.js';
import mediaQuery from '../../utils/mediaQuery.js';
import { useParentContext, applyMediaStyle } from '../ParentContext/ParentContextProvider.js';
import { useThemeContext } from '../ThemeProvider/ThemeContext.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function Step(_ref) {
  var theme = _ref.theme,
      current = _ref.current,
      active = _ref.active,
      last = _ref.last,
      currentSubStep = _ref.currentSubStep,
      icon = _ref.icon,
      gatewayIcon = _ref.gatewayIcon,
      label = _ref.label,
      children = _ref.children,
      otherProperties = _objectWithoutProperties(_ref, ["theme", "current", "active", "last", "currentSubStep", "icon", "gatewayIcon", "label", "children"]);

  var themeTokens = useThemeContext();
  var parentContext = useParentContext();
  var mediaQueryDown = mediaQuery('md', true);
  var mediaQueryUp = mediaQuery('md');
  var stepStyle = useMemo(function () {
    return merge.all([progressMultiStep.multiStep__step, applyMediaStyle(parentContext, progressMultiStep, mediaQueryDown, 'multiStep__step'), theme]);
  }, [mediaQueryDown, parentContext, theme]);
  var dotBarStyle = useMemo(function () {
    return merge.all([progressMultiStep.multiStep__step__dotBar, applyMediaStyle(parentContext, progressMultiStep, mediaQueryUp, 'multiStep__step__dotBar'), current ? progressMultiStep.multiStep__step_current__dotBar : {}]);
  }, [current, mediaQueryUp, parentContext]);
  var dotStyle = useMemo(function () {
    if (current) {
      return merge.all([progressMultiStep.multiStep__step__dot, progressMultiStep.multiStep__step_current__dot, progressMultiStep.multiStep__step_current____dot, themeTokens.secondaryColor ? {
        backgroundColor: themeTokens.secondaryColor,
        borderColor: themeTokens.secondaryColor
      } : {}]);
    }

    if (active) {
      return merge.all([progressMultiStep.multiStep__step__dot, progressMultiStep.multiStep__step_active__dot, themeTokens.primaryColor ? {
        backgroundColor: themeTokens.primaryColor
      } : {}]);
    }

    return progressMultiStep.multiStep__step__dot;
  }, [active, current, themeTokens.primaryColor, themeTokens.secondaryColor]);
  var progressBarStyle = useMemo(function () {
    return merge.all([progressMultiStep.multiStep__step__progressBar, applyMediaStyle(parentContext, progressMultiStep, mediaQueryDown, 'multiStep__step__progressBar')]);
  }, [mediaQueryDown, parentContext]);
  var stepLabel = useMemo(function () {
    var baseStepLabel = merge.all([progressMultiStep.multiStep__step__dotLabel, body1, applyMediaStyle(parentContext, hiddenSmDown, Object.keys(hiddenSmDown)[0])]);

    if (current) {
      return merge.all([baseStepLabel, progressMultiStep.multiStep__step_current__dotLabel, themeTokens.secondaryColor ? {
        color: themeTokens.secondaryColor
      } : {}]);
    }

    if (active) {
      return merge.all([baseStepLabel, progressMultiStep.multiStep__step_active__dotLabel, themeTokens.primaryColor ? {
        color: themeTokens.primaryColor
      } : {}]);
    }

    return baseStepLabel;
  }, [active, current, parentContext, themeTokens.primaryColor, themeTokens.secondaryColor]);
  var progressTrackStyle = useMemo(function () {
    var childrenLength = React.Children.count(children);
    var width = currentSubStep && children ? "".concat(Math.min(currentSubStep, childrenLength) * 100 / (childrenLength + 1), "%") : '0';

    if (current) {
      return merge.all([progressBarStyleObject.progressBar__progressTrack, {
        width: width
      }, progressMultiStep.multiStep__step_current__progressBar__progressTrack, themeTokens.secondaryColor ? {
        backgroundColor: themeTokens.secondaryColor
      } : {}]);
    }

    if (active) {
      return merge.all([progressBarStyleObject.progressBar__progressTrack, {
        width: width
      }, progressMultiStep.multiStep__step_active__progressBar__progressTrack, themeTokens.primaryColor ? {
        backgroundColor: themeTokens.primaryColor
      } : {}]);
    }

    return merge.all([progressBarStyleObject.progressBar__progressTrack, {
      width: width
    }, themeTokens.primaryColor ? {
      backgroundColor: themeTokens.primaryColor
    } : {}]);
  }, [active, children, current, currentSubStep, themeTokens.primaryColor, themeTokens.secondaryColor]);
  var stepIcon = useMemo(function () {
    if (icon) {
      var iconStyle = merge.all([progressMultiStep.multiStep__stepIcon, applyMediaStyle(parentContext, hiddenSmDown, Object.keys(hiddenSmDown)[0])]);

      if (current) {
        iconStyle.fill = themeTokens.secondaryColor || progressMultiStep.multiStep__step_current__stepIcon.color;
      } else if (active) {
        iconStyle.fill = themeTokens.primaryColor || progressMultiStep.multiStep__step_active__stepIcon.color;
      } else {
        iconStyle.fill = iconStyle.color;
      }

      return /*#__PURE__*/React.cloneElement(icon, _objectSpread(_objectSpread({}, icon.props), {}, {
        theme: iconStyle,
        size: 'sm',
        filled: current
      }));
    }

    return icon;
  }, [active, current, icon, parentContext, themeTokens.primaryColor, themeTokens.secondaryColor]);
  var stepGatewayIcon = useMemo(function () {
    if (gatewayIcon) {
      var gatewayIconStyle = merge.all([progressMultiStep.multiStep__step__dotBar__icon, applyMediaStyle(parentContext, progressMultiStep, mediaQueryDown, 'multiStep__step__dotBar__icon')]);

      if (active) {
        gatewayIconStyle.fill = themeTokens.primaryColor || progressMultiStep.multiStep__step_active__dotBar__icon.color;
      } else {
        gatewayIconStyle.fill = gatewayIconStyle.color;
      }

      return /*#__PURE__*/React.cloneElement(gatewayIcon, _objectSpread(_objectSpread({}, gatewayIcon.props), {}, {
        theme: gatewayIconStyle,
        size: 'md'
      }));
    }

    return gatewayIcon;
  }, [active, gatewayIcon, mediaQueryDown, parentContext, themeTokens.primaryColor]);
  var subDotBarStyle = useMemo(function () {
    return merge.all([progressMultiStep.multiStep__step__subDotBar, _defineProperty({}, mediaQueryUp, progressMultiStep[mediaQueryUp].multiStep__step__subDotBar)]);
  }, [mediaQueryUp]);

  if (last) {
    return jsx("li", _extends({
      css: stepStyle,
      role: "presentation"
    }, otherProperties), stepIcon, jsx("div", {
      css: dotStyle
    }), jsx("span", {
      css: stepLabel
    }, label));
  }

  return jsx("li", _extends({
    css: stepStyle,
    role: "presentation"
  }, otherProperties), stepIcon, jsx("div", {
    css: dotBarStyle
  }, jsx("div", {
    css: dotStyle
  }), jsx("div", {
    css: progressBarStyle
  }, jsx("div", {
    css: progressTrackStyle
  }), children && jsx("div", {
    css: subDotBarStyle
  }, React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
      current: index <= currentSubStep - 1,
      active: active
    }));
  }))), stepGatewayIcon), jsx("span", {
    css: stepLabel
  }, label));
}
Step.defaultProps = {
  theme: {},
  current: false,
  active: false,
  last: false,
  currentSubStep: undefined,
  icon: undefined,
  gatewayIcon: undefined,
  children: undefined
};

export default Step;
